import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../store/useAuthStore';
import { NAV_ENTRIES, NAV_SECTIONS } from './NavConfig';

/**
 * NavList — the role-gated, grouped navigation list shared by the desktop
 * sidebar and the mobile Drawer in `ForgeShell`. Single source of nav markup
 * so the two surfaces can never drift.
 *
 * `layoutId` must be unique per rendered instance: framer-motion animates the
 * active-pill between links sharing a `layoutId`, and two lists mounted at once
 * (sidebar hidden behind an open Drawer) with the same id would fight.
 */
export function NavList({
  pathname,
  layoutId,
  onNavigate,
  className = '',
}: {
  pathname: string;
  layoutId: string;
  onNavigate?: () => void;
  className?: string;
}) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'trainee';
  const visible = NAV_ENTRIES.filter((e) => !e.roles || e.roles.includes(role));

  return (
    <nav aria-label="Main" className={`flex flex-col gap-5 ${className}`}>
      {NAV_SECTIONS.map((section) => {
        const items = visible.filter((e) => e.section === section);
        if (items.length === 0) return null;
        return (
          <div key={section}>
            <div className="eyebrow mb-1.5 px-3 text-[10px]">{section}</div>
            <div className="flex flex-col gap-0.5">
              {items.map((item) => {
                const active =
                  item.path === '/' ? pathname === '/' : pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path + item.label}
                    to={item.path as never}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={`group relative flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-3 text-[13.5px] font-medium transition-colors duration-150 ${
                      active ? 'text-neon' : 'text-txt-2 hover:bg-card hover:text-txt'
                    }`}
                  >
                    {active && (
                      <motion.span
                        layoutId={layoutId}
                        className="absolute inset-0 rounded-lg bg-neon-glow ring-1 ring-inset ring-neon/15"
                        transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                      />
                    )}
                    <Icon
                      className={`relative size-[17px] shrink-0 transition-colors ${active ? 'text-neon' : 'text-txt-muted group-hover:text-txt-2'}`}
                    />
                    <span className="relative flex-1 truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}
